import { MAX_WIDTH } from 'constants/maxWidth'
import React, { Dispatch, SetStateAction, forwardRef, useMemo } from 'react'
import { ImageBackground, StyleSheet } from 'react-native'
import { useSafeAreaInsets } from 'react-native-safe-area-context'

import { Box } from 'widgets/Box'
import { Image } from 'widgets/Image'
import { Video } from 'widgets/Video'

import { useMaxWidth } from 'hooks/useMaxWidth'

import { getTransformedMedia } from 'utils/files/fileTransformations'
import { getFileTypeFromMimeType } from 'utils/media'
import commonStyles from 'utils/styles/commonStyles'

import defaultStepFile from 'assets/images/defaultStepFile.png'
import upspaceChalkWordmark from 'assets/images/upspace-chalk-wordmark.png'

import { Media } from 'types/media'

import { VideoProgress } from '../types'

export const MediaPlayer = forwardRef(
  (
    {
      file,
      onVideoProgress,
      autoplay = true,
      muted = false,
      isVideo,
      hideVideoPlayPauseButton,
      isPreview,
      setIsPreview,
    }: {
      file?: Media
      onVideoProgress?: (state: VideoProgress) => void
      autoplay?: boolean
      muted?: boolean
      isVideo?: boolean
      hideVideoPlayPauseButton?: boolean
      isPreview: boolean
      setIsPreview?: Dispatch<SetStateAction<boolean>>
    },
    ref,
  ) => {
    const { width, height } = useMaxWidth()
    const { top: topInset } = useSafeAreaInsets()

    const styles = StyleSheet.create({
      wordmarkContainer: {
        position: 'absolute',
        top: 24 + (topInset !== 0 ? topInset : 24),
        zIndex: 2,
        marginLeft: 12,
      },
    })

    const mimeType = file?.mimeType ?? 'image'
    // eslint-disable-next-line no-param-reassign
    isVideo = useMemo(
      () => isVideo ?? getFileTypeFromMimeType(mimeType) === 'video',
      [isVideo, mimeType],
    )

    if (isVideo) {
      return (
        <Video
          muted={muted}
          ref={ref}
          width={width}
          height={height}
          source={file?.url ?? ''}
          autoplay={autoplay}
          onProgress={onVideoProgress}
          hidePlayPauseButton={hideVideoPlayPauseButton}
          isPreview={isPreview}
          setIsPreview={setIsPreview}
        />
      )
    }

    return (
      <>
        {!file && (
          <Box height={100} width='40%' style={styles.wordmarkContainer}>
            <Image
              source={upspaceChalkWordmark}
              cacheKey='upspaceChalkWordmark'
              imageWidth={200}
              resizeMode='contain'
              style={commonStyles.fullSize}
            />
          </Box>
        )}
        <Box
          testID='ImageMediaPlayer'
          width='100%'
          height='100%'
          justifyContent='center'
          backgroundColor='black'
        >
          <ImageBackground
            resizeMode='cover'
            style={commonStyles.fullSize}
            source={
              file
                ? { uri: getTransformedMedia('image', file.url, MAX_WIDTH * 2) }
                : defaultStepFile
            }
          />
        </Box>
      </>
    )
  },
)
